import React from 'react';
import DicaDeCampo from 'components/common/DicaDeCampo';
import {
  BarChart3, TrendingUp, TrendingDown, DollarSign, Activity, Target
} from 'lucide-react';

const MetricasComerciais = ({ dadosComerciais }) => {
  // Quatro das cinco cores eram ilegíveis no tema claro sobre `--surface-muted`, medido:
  // amber-500 1,96:1 · violet-500 3,87:1 · --status-warn 2,91:1 · --status-good 3,44:1 — todas
  // abaixo dos 4,5:1 exigidos para texto normal. As variantes `-strong` foram escolhidas por
  // medição (6,47 / 5,01) e funcionam nos dois temas.
  //
  // Consequência assumida: sobram três cores distintas para cinco cards, então dois pares
  // coincidem. O cartão continua diferenciado pelo ícone e pelo rótulo. Cinco cores decorativas
  // acessíveis exigiriam uma paleta própria, que o projeto não tem — e inventá-la aqui seria
  // decisão de design, não correção de contraste.
  const CORES = {
    azul: {
      bg: 'bg-[color:var(--surface-muted)]',
      texto: 'text-[color:var(--accent)]',
      icone: 'text-[color:var(--accent)]',
    },
    indigo: {
      bg: 'bg-[color:var(--surface-muted)]',
      texto: 'text-[color:var(--accent)]',
      icone: 'text-[color:var(--accent)]',
    },
    verde: {
      bg: 'bg-[color:var(--surface-muted)]',
      texto: 'text-[color:var(--status-good-strong)]',
      icone: 'text-[color:var(--status-good-strong)]',
    },
    cyan: {
      bg: 'bg-[color:var(--surface-muted)]',
      texto: 'text-[color:var(--status-warning-strong)]',
      icone: 'text-[color:var(--status-warning-strong)]',
    },
    purple: {
      bg: 'bg-[color:var(--surface-muted)]',
      texto: 'text-[color:var(--status-warning-strong)]',
      icone: 'text-[color:var(--status-warning-strong)]',
    },
  };

  const formatarValor = (valor) => {
    if (!valor && valor !== 0) return 'R$ 0';
    
    if (valor >= 1000000) {
      return `R$ ${(valor / 1000000).toFixed(1)}M`;
    } else if (valor >= 1000) {
      return `R$ ${(valor / 1000).toFixed(0)}K`;
    }
    return `R$ ${valor.toFixed(0)}`;
  };

  const calcularVariacao = (atual, projetado) => {
    if (!atual || !projetado || atual === 0) return 0;
    return Math.round(((projetado - atual) / atual) * 100);
  };

  /*
    OS CARDS DE PROJEÇÃO SOMEM quando o período pedido já fechou ou ainda não começou.

    O backend devolve `null` nesses casos — "não se projeta" —, e exibir zero afirmaria outra coisa:
    que a estimativa é de fechar em zero. Antes desta mudança a tela mostrava uma projeção calculada
    sobre o mês CORRENTE ao lado de números de um mês passado; pedir julho no dia 1º de agosto
    multiplicava o realizado por 31.

    A lista é montada condicionalmente e os layouts selecionam por `id`, nunca por posição: com dois
    cards a menos, qualquer índice fixo apontaria para o card errado ou para `undefined`.
  */
  const temProjecao = dadosComerciais?.projecaoTotalVendas !== null
    && dadosComerciais?.projecaoTotalVendas !== undefined;

  const metricas = [
    {
      id: 'total-vendas',
      titulo: 'Total de Vendas',
      valor: dadosComerciais?.totalVendas?.toLocaleString('pt-BR') || '0',
      subtitulo: 'Contratos fechados',
      dica: (
        <>
          <strong className="font-semibold text-[color:var(--text-primary)]">Contratos instalados no período</strong>, contados pela data de ativação.
          <span className="mt-1.5 block">Não filtra situação do contrato e aceita contrato sem vendedor. Ficam de fora Troca de Titularidade, Reativação e Vendedor padrão.</span>
          <span className="mt-1.5 block">A aba <strong className="font-medium text-[color:var(--text-primary)]">Comissão</strong> conta outra população: a venda do mês, pela data de cadastro, e só contrato ativo. Os dois números não batem, e isso é regra.</span>
        </>
      ),
      variacao: null,
      icone: BarChart3,
      cor: CORES.indigo
    },
    ...(temProjecao ? [{
      id: 'projecao-vendas',
      titulo: 'Projeção Total de Vendas',
      valor: dadosComerciais?.projecaoTotalVendas?.toLocaleString('pt-BR') || '0',
      subtitulo: 'Projeção',
      dica: (
        <>
          Vendas de hoje multiplicadas por <strong className="font-medium text-[color:var(--text-primary)]">dias do mês ÷ dia de hoje</strong>.
          <span className="mt-1.5 block">Supõe o ritmo atual até o fim do mês. Só aparece com o período em andamento; período fechado não recebe projeção.</span>
        </>
      ),
      variacao: calcularVariacao(dadosComerciais?.totalVendas, dadosComerciais?.projecaoTotalVendas),
      icone: TrendingUp,
      cor: CORES.cyan
    }] : []),
    {
      id: 'total-receita',
      titulo: 'Total Receita de Vendas',
      valor: formatarValor(dadosComerciais?.totalReceitaVendas),
      subtitulo: 'Receita acumulada',
      dica: (
        <>
          Soma do valor dos planos dos mesmos contratos do card <strong className="font-medium text-[color:var(--text-primary)]">Total de Vendas</strong>.
          <span className="mt-1.5 block">É receita contratada no mês, não recebimento: faturamento e inadimplência não entram aqui.</span>
        </>
      ),
      variacao: null,
      icone: DollarSign,
      cor: CORES.verde
    },
    ...(temProjecao ? [{
      id: 'projecao-receita',
      titulo: 'Projeção Receita de Vendas',
      valor: formatarValor(dadosComerciais?.projecaoReceitaVendas),
      subtitulo: 'Projeção',
      dica: (
        <>
          Receita de hoje multiplicada por <strong className="font-medium text-[color:var(--text-primary)]">dias do mês ÷ dia de hoje</strong>, no mesmo critério da projeção de vendas.
        </>
      ),
      variacao: calcularVariacao(dadosComerciais?.totalReceitaVendas, dadosComerciais?.projecaoReceitaVendas),
      icone: Activity,
      cor: CORES.azul
    }] : []),
    {
      id: 'ticket-medio',
      titulo: 'Ticket Médio',
      valor: formatarValor(dadosComerciais?.ticketMedio),
      subtitulo: 'Valor médio por venda',
      dica: (
        <>
          Receita dividida pelo total de vendas do período.
          <span className="mt-1.5 block">Entram todos os planos da mesma população, inclusive os de menor valor, então o ticket cai quando eles crescem.</span>
        </>
      ),
      variacao: null,
      icone: Target,
      cor: CORES.purple
    }
  ];

  const porId = (...ids) => ids.map((id) => metricas.find((m) => m.id === id)).filter(Boolean);
  const projecoes = porId('projecao-vendas', 'projecao-receita');
  const ticketMetrica = metricas.find((m) => m.id === 'ticket-medio');

  return (
    <div className="mb-8">
      {/* Layout Mobile: Organização lógica com ticket em destaque */}
      <div className="block md:hidden">
        <div className="space-y-4">
          {/* Primeira linha: Total Vendas + Total Receita */}
          <div className="grid grid-cols-2 gap-4">
            {porId('total-vendas', 'total-receita').map((metrica, index) => (
              <div
                key={index}
                className="bg-[color:var(--surface)] rounded-2xl shadow-sm border border-[color:var(--border)] p-4 hover:shadow-md transition-shadow duration-200"
              >
                <div className="flex flex-col h-full">
                  {/* Header com ícone */}
                  <div className="flex items-center justify-between mb-3">
                    <div className={`p-2.5 rounded-xl ${metrica.cor.bg} shadow-sm`}>
                      <metrica.icone 
                        className={`h-5 w-5 ${metrica.cor.icone}`} 
                        strokeWidth={1.5}
                      />
                    </div>
                  </div>
                  
                  {/* Conteúdo principal */}
                  <div className="flex-1 space-y-2">
                    <div className="flex items-start gap-1.5">
                      <p className="text-xs font-semibold text-[color:var(--text-muted)] uppercase tracking-wide leading-tight">
                        {metrica.titulo}
                      </p>
                      {metrica.dica && (
                        <DicaDeCampo id={`${metrica.id}-dica`} titulo={metrica.titulo} alinhamento="esquerda">
                          {metrica.dica}
                        </DicaDeCampo>
                      )}
                    </div>
                    
                    <p className={`text-xl font-bold ${metrica.cor.texto} leading-none`}>
                      {metrica.valor}
                    </p>
                    
                    <div className="space-y-1.5">
                      <p className="text-xs text-[color:var(--text-muted)] leading-tight">
                        {metrica.subtitulo}
                      </p>
                      
                      {metrica.variacao !== null && metrica.variacao !== 0 && (
                        <div className="flex items-center">
                          <span className={`text-xs font-semibold px-2 py-1 rounded-full ${metrica.variacao > 0 ? 'bg-[color:var(--surface-muted)] text-[color:var(--status-good)] border border-[color:var(--border)]' : 'bg-[color:var(--surface-muted)] text-[color:var(--status-bad)] border border-[color:var(--border)]'}`}>
                            {metrica.variacao > 0 ? <TrendingUp className="inline h-3.5 w-3.5 mr-0.5" /> : <TrendingDown className="inline h-3.5 w-3.5 mr-0.5" />}{metrica.variacao > 0 ? '+' : ''}{metrica.variacao}%
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Segunda linha: Projeção Vendas + Projeção Receita — ausente em período fechado */}
          {projecoes.length > 0 && (
          <div className="grid grid-cols-2 gap-4">
            {projecoes.map((metrica, index) => (
              <div
                key={index + 2}
                className="bg-[color:var(--surface)] rounded-2xl shadow-sm border border-[color:var(--border)] p-4 hover:shadow-md transition-shadow duration-200"
              >
                <div className="flex flex-col h-full">
                  {/* Header com ícone */}
                  <div className="flex items-center justify-between mb-3">
                    <div className={`p-2.5 rounded-xl ${metrica.cor.bg} shadow-sm`}>
                      <metrica.icone 
                        className={`h-5 w-5 ${metrica.cor.icone}`} 
                        strokeWidth={1.5}
                      />
                    </div>
                  </div>
                  
                  {/* Conteúdo principal */}
                  <div className="flex-1 space-y-2">
                    <div className="flex items-start gap-1.5">
                      <p className="text-xs font-semibold text-[color:var(--text-muted)] uppercase tracking-wide leading-tight">
                        {metrica.titulo}
                      </p>
                      {metrica.dica && (
                        <DicaDeCampo id={`${metrica.id}-dica`} titulo={metrica.titulo} alinhamento="esquerda">
                          {metrica.dica}
                        </DicaDeCampo>
                      )}
                    </div>
                    
                    <p className={`text-xl font-bold ${metrica.cor.texto} leading-none`}>
                      {metrica.valor}
                    </p>
                    
                    <div className="space-y-1.5">
                      <p className="text-xs text-[color:var(--text-muted)] leading-tight">
                        {metrica.subtitulo}
                      </p>
                      
                      {metrica.variacao !== null && metrica.variacao !== 0 && (
                        <div className="flex items-center">
                          <span className={`text-xs font-semibold px-2 py-1 rounded-full ${metrica.variacao > 0 ? 'bg-[color:var(--surface-muted)] text-[color:var(--status-good)] border border-[color:var(--border)]' : 'bg-[color:var(--surface-muted)] text-[color:var(--status-bad)] border border-[color:var(--border)]'}`}>
                            {metrica.variacao > 0 ? <TrendingUp className="inline h-3.5 w-3.5 mr-0.5" /> : <TrendingDown className="inline h-3.5 w-3.5 mr-0.5" />}{metrica.variacao > 0 ? '+' : ''}{metrica.variacao}%
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          )}

          {/* Terceira linha: Ticket Médio com largura dupla */}
          <div className="w-full">
            {(() => {
              const IconeComponent = ticketMetrica.icone;
              
              return (
                <div 
                  className="bg-[color:var(--surface)] rounded-xl shadow-sm border border-[color:var(--border)] p-4 hover:shadow-md transition-shadow duration-200"
                >
                  <div className="flex flex-col h-full">
                    {/* Header com ícone */}
                    <div className="flex items-center justify-between mb-3">
                      <div className={`p-2.5 rounded-xl ${ticketMetrica.cor.bg} shadow-sm`}>
                        <IconeComponent 
                          className={`h-5 w-5 ${ticketMetrica.cor.icone}`} 
                          strokeWidth={1.5}
                        />
                      </div>
                    </div>
                    
                    {/* Conteúdo principal */}
                    <div className="flex-1 space-y-2">
                      <div className="flex items-start gap-1.5">
                        <p className="text-xs font-semibold text-[color:var(--text-muted)] uppercase tracking-wide leading-tight">
                          {ticketMetrica.titulo}
                        </p>
                        {ticketMetrica.dica && (
                          <DicaDeCampo id="ticket-medio-dica-destaque" titulo={ticketMetrica.titulo} alinhamento="esquerda">
                            {ticketMetrica.dica}
                          </DicaDeCampo>
                        )}
                      </div>
                      
                      <p className={`text-xl font-bold ${ticketMetrica.cor.texto} leading-none`}>
                        {ticketMetrica.valor}
                      </p>
                      
                      <div className="space-y-1.5">
                        <p className="text-xs text-[color:var(--text-muted)] leading-tight">
                          {ticketMetrica.subtitulo}
                        </p>
                        
                        {ticketMetrica.variacao !== null && ticketMetrica.variacao !== 0 && (
                          <div className="flex items-center">
                            <span className={`text-xs font-semibold px-2 py-1 rounded-full ${ticketMetrica.variacao > 0 ? 'bg-[color:var(--surface-muted)] text-[color:var(--status-good)] border border-[color:var(--border)]' : 'bg-[color:var(--surface-muted)] text-[color:var(--status-bad)] border border-[color:var(--border)]'}`}>
                              {ticketMetrica.variacao > 0 ? '↗' : '↘'} {ticketMetrica.variacao > 0 ? '+' : ''}{ticketMetrica.variacao}%
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      </div>

      {/* Layout Desktop: Grid otimizado */}
      <div className="hidden md:block">
        {/* Primeira linha: 3 cards principais */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
          {porId('total-vendas', 'total-receita', 'ticket-medio').map((metrica, index) => (
            <div
              key={index}
              className="bg-[color:var(--surface)] rounded-2xl shadow-sm border border-[color:var(--border)] p-6 hover:shadow-md transition-shadow duration-200"
            >
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <div className="mb-3 flex items-start gap-1.5">
                    <p className="text-sm font-semibold text-[color:var(--text-muted)] uppercase tracking-wide">
                      {metrica.titulo}
                    </p>
                    {metrica.dica && (
                      <DicaDeCampo id={`${metrica.id}-dica-amplo`} titulo={metrica.titulo} alinhamento="esquerda">
                        {metrica.dica}
                      </DicaDeCampo>
                    )}
                  </div>
                  <p className={`text-3xl font-bold ${metrica.cor.texto} mb-3 leading-none`}>
                    {metrica.valor}
                  </p>
                  <div className="space-y-2">
                    <p className="text-sm text-[color:var(--text-muted)]">
                      {metrica.subtitulo}
                    </p>
                    {metrica.variacao !== null && metrica.variacao !== 0 && (
                      <span className={`text-sm font-semibold px-3 py-1.5 rounded-full inline-block ${metrica.variacao > 0 ? 'bg-[color:var(--surface-muted)] text-[color:var(--status-good)] border border-[color:var(--border)]' : 'bg-[color:var(--surface-muted)] text-[color:var(--status-bad)] border border-[color:var(--border)]'}`}>
                        {metrica.variacao > 0 ? <TrendingUp className="inline h-3.5 w-3.5 mr-0.5" /> : <TrendingDown className="inline h-3.5 w-3.5 mr-0.5" />}{metrica.variacao > 0 ? '+' : ''}{metrica.variacao}%
                      </span>
                    )}
                  </div>
                </div>
                <div className={`p-4 rounded-xl ${metrica.cor.bg} shadow-sm flex-shrink-0 ml-6`}>
                  <metrica.icone 
                    className={`h-7 w-7 ${metrica.cor.icone}`} 
                    strokeWidth={1.5}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Segunda linha: os 2 cards de projeção — ausente em período fechado */}
        {projecoes.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {projecoes.map((metrica, index) => (
            <div
              key={index + 3}
              className="bg-[color:var(--surface)] rounded-2xl shadow-sm border border-[color:var(--border)] p-6 hover:shadow-md transition-shadow duration-200"
            >
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <div className="mb-3 flex items-start gap-1.5">
                    <p className="text-sm font-semibold text-[color:var(--text-muted)] uppercase tracking-wide">
                      {metrica.titulo}
                    </p>
                    {metrica.dica && (
                      <DicaDeCampo id={`${metrica.id}-dica-amplo`} titulo={metrica.titulo} alinhamento="esquerda">
                        {metrica.dica}
                      </DicaDeCampo>
                    )}
                  </div>
                  <p className={`text-3xl font-bold ${metrica.cor.texto} mb-3 leading-none`}>
                    {metrica.valor}
                  </p>
                  <div className="space-y-2">
                    <p className="text-sm text-[color:var(--text-muted)]">
                      {metrica.subtitulo}
                    </p>
                    {metrica.variacao !== null && metrica.variacao !== 0 && (
                      <span className={`text-sm font-semibold px-3 py-1.5 rounded-full inline-block ${metrica.variacao > 0 ? 'bg-[color:var(--surface-muted)] text-[color:var(--status-good)] border border-[color:var(--border)]' : 'bg-[color:var(--surface-muted)] text-[color:var(--status-bad)] border border-[color:var(--border)]'}`}>
                        {metrica.variacao > 0 ? <TrendingUp className="inline h-3.5 w-3.5 mr-0.5" /> : <TrendingDown className="inline h-3.5 w-3.5 mr-0.5" />}{metrica.variacao > 0 ? '+' : ''}{metrica.variacao}%
                      </span>
                    )}
                  </div>
                </div>
                <div className={`p-4 rounded-xl ${metrica.cor.bg} shadow-sm flex-shrink-0 ml-6`}>
                  <metrica.icone 
                    className={`h-7 w-7 ${metrica.cor.icone}`} 
                    strokeWidth={1.5}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
        )}
      </div>
    </div>
  );
};

export default MetricasComerciais;



